/**
 * useJokers - Quiz Joker/Lifeline Management
 * 
 * Handles all four joker types:
 * 1. 50:50 Joker - Hides 2 wrong answers
 * 2. Audience Joker - Shows answer distribution
 * 3. Best Player Joker - Shows top player's answer
 * 4. Time Extension Joker - Adds bonus time (+20 seconds)
 * 
 * Uses unifiedDataService for all RTDB operations.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { RealtimeDBService } from '../../../firebase/unifiedDataService';
import { Logger } from '../../../utils/logger';

const log = Logger.create('useJokers');

// Joker type constants
export const JOKER_TYPES = {
  FIFTY_FIFTY: 'fiftyFifty',
  AUDIENCE: 'audience',
  BEST_PLAYER: 'bestPlayer',
  TIME_EXTENSION: 'timeExtension',
};

// Default jokers available per quiz
export const DEFAULT_JOKERS = {
  [JOKER_TYPES.FIFTY_FIFTY]: true,
  [JOKER_TYPES.AUDIENCE]: true,
  [JOKER_TYPES.BEST_PLAYER]: true,
  [JOKER_TYPES.TIME_EXTENSION]: true,
};

/**
 * Custom hook for joker/lifeline management
 * @param {string} instancePath - The full Firebase path for the quiz instance
 * @param {string} participantId - The participant's ID
 * @param {Object} currentQuestion - The current question object
 * @param {number} currentQuestionIndex - Current question index
 * @param {Object} settings - Quiz settings
 * @param {Array} participants - All participants in session
 * @param {Function} onTimeExtension - Callback when time extension is used
 * @returns {Object} Joker state and actions
 */
export function useJokers(
  instancePath,
  participantId,
  currentQuestion,
  currentQuestionIndex,
  settings = {},
  participants = [],
  onTimeExtension = null
) {
  // Joker availability state
  const [availableJokers, setAvailableJokers] = useState({ ...DEFAULT_JOKERS });
  
  // Active joker effects
  const [hiddenOptions, setHiddenOptions] = useState([]);
  const [audienceResults, setAudienceResults] = useState(null);
  const [bestPlayerAnswer, setBestPlayerAnswer] = useState(null);
  
  // Loading states
  const [jokerLoading, setJokerLoading] = useState({});
  
  // Track jokers used per question (for reset on new question)
  const questionJokersRef = useRef({});
  
  // Unsubscribe function refs for cleanup
  const bestPlayerUnsubscribeRef = useRef(null);
  const audienceUnsubscribeRef = useRef(null);
  
  // Track current question to detect changes
  const lastQuestionIndexRef = useRef(currentQuestionIndex);

  /**
   * Cleanup best player listener
   */
  const cleanupBestPlayerListener = useCallback(() => {
    if (bestPlayerUnsubscribeRef.current) {
      log.debug('Cleaning up best player listener');
      bestPlayerUnsubscribeRef.current();
      bestPlayerUnsubscribeRef.current = null;
    }
  }, []);

  /**
   * Cleanup audience listener
   */
  const cleanupAudienceListener = useCallback(() => {
    if (audienceUnsubscribeRef.current) {
      log.debug('Cleaning up audience listener');
      audienceUnsubscribeRef.current();
      audienceUnsubscribeRef.current = null;
    }
  }, []);

  /**
   * Reset joker effects when question changes
   */
  const resetQuestionEffects = useCallback(() => {
    setHiddenOptions([]);
    setAudienceResults(null);
    setBestPlayerAnswer(null);
    questionJokersRef.current = {};
    cleanupBestPlayerListener();
    cleanupAudienceListener();
  }, [cleanupBestPlayerListener, cleanupAudienceListener]);
  
  // Auto-cleanup when question changes
  useEffect(() => {
    if (lastQuestionIndexRef.current !== currentQuestionIndex) {
      resetQuestionEffects();
      lastQuestionIndexRef.current = currentQuestionIndex;
    }
  }, [currentQuestionIndex, resetQuestionEffects]);
  
  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      cleanupBestPlayerListener();
      cleanupAudienceListener();
    };
  }, [cleanupBestPlayerListener, cleanupAudienceListener]);

  /**
   * Initialize joker state from storage
   */
  const initializeJokers = useCallback((usedJokers) => {
    if (usedJokers && typeof usedJokers === 'object') {
      const available = { ...DEFAULT_JOKERS };
      Object.keys(usedJokers).forEach(jokerType => {
        if (usedJokers[jokerType]) {
          available[jokerType] = false;
        }
      });
      setAvailableJokers(available);
    }
  }, []);

  /**
   * Mark joker as used
   */
  const useJoker = useCallback(async (jokerType) => {
    if (!availableJokers[jokerType]) return false;

    // Update local state
    setAvailableJokers(prev => ({
      ...prev,
      [jokerType]: false
    }));

    // Save to Firebase using RealtimeDBService
    try {
      if (instancePath) {
        const jokerPath = `${instancePath}/jokersUsed/${participantId}/${jokerType}`;
        await RealtimeDBService.set(jokerPath, true);
      }
    } catch (error) {
      log.error('Error saving joker usage:', error);
    }

    return true;
  }, [instancePath, participantId, availableJokers]);

  /**
   * 50:50 Joker - Hide 2 wrong answers
   */
  const useFiftyFiftyJoker = useCallback(async () => {
    if (!currentQuestion || !availableJokers[JOKER_TYPES.FIFTY_FIFTY]) return false;

    setJokerLoading(prev => ({ ...prev, [JOKER_TYPES.FIFTY_FIFTY]: true }));

    try {
      const correctIndices = currentQuestion.correctAnswerIndices || 
        [currentQuestion.correctAnswerIndex];
      
      const allIndices = currentQuestion.options.map((_, idx) => idx);
      const wrongIndices = allIndices.filter(idx => !correctIndices.includes(idx));
      
      // Randomly select 2 wrong answers to hide (or all if fewer)
      const shuffled = wrongIndices.sort(() => Math.random() - 0.5);
      const toHide = shuffled.slice(0, Math.min(2, shuffled.length));
      
      setHiddenOptions(toHide);
      await useJoker(JOKER_TYPES.FIFTY_FIFTY);
      
      return true;
    } catch (error) {
      log.error('Error using 50:50 joker:', error);
      return false;
    } finally {
      setJokerLoading(prev => ({ ...prev, [JOKER_TYPES.FIFTY_FIFTY]: false }));
    }
  }, [currentQuestion, availableJokers, useJoker]);

  /**
   * Audience Joker - Get answer distribution from other participants
   * Uses realtime listener to update percentages as new answers come in
   */
  const useAudienceJoker = useCallback(async () => {
    if (!currentQuestion || !availableJokers[JOKER_TYPES.AUDIENCE]) return false;

    setJokerLoading(prev => ({ ...prev, [JOKER_TYPES.AUDIENCE]: true }));

    try {
      if (!instancePath) return false;

      // Calculate vote percentages and counts from answer data
      const calculateVotePercentages = (questionAnswers) => {
        const voteCounts = {};
        currentQuestion.options.forEach((_, idx) => {
          voteCounts[idx] = 0;
        });

        let totalVotes = 0;
        Object.keys(questionAnswers || {}).forEach(pid => {
          if (pid !== participantId) {
            const answerData = questionAnswers[pid];
            // Get selected indices from the answer data
            const selectedIndices = answerData.selectedIndices || 
              (answerData.selectedIndex !== null && answerData.selectedIndex !== undefined 
                ? [answerData.selectedIndex] 
                : []);
            
            selectedIndices.forEach(idx => {
              if (voteCounts[idx] !== undefined) {
                voteCounts[idx]++;
                totalVotes++;
              }
            });
          }
        });

        // Return both percentages and absolute counts
        const percentages = {};
        const counts = {};
        Object.keys(voteCounts).forEach(idx => {
          counts[idx] = voteCounts[idx];
          percentages[idx] = totalVotes > 0 
            ? Math.round((voteCounts[idx] / totalVotes) * 100)
            : 0; // Show 0% if no one has answered yet
        });

        return { 
          percentages, 
          counts,
          totalVotes 
        };
      };

      // First get initial data to set results immediately
      const answersPath = `${instancePath}/answers/${currentQuestionIndex}`;
      const initialData = await RealtimeDBService.get(answersPath);
      
      const initialCalc = calculateVotePercentages(initialData);
      setAudienceResults({
        percentages: initialCalc.percentages,
        counts: initialCalc.counts,
        totalVotes: initialCalc.totalVotes
      });
      setJokerLoading(prev => ({ ...prev, [JOKER_TYPES.AUDIENCE]: false }));
      
      log.debug(`Audience joker initial: ${initialCalc.totalVotes} total votes`, initialCalc);

      // Then set up realtime listener for updates
      const unsubscribe = RealtimeDBService.subscribe(answersPath, (questionAnswers) => {
        const { percentages, counts, totalVotes } = calculateVotePercentages(questionAnswers);
        log.debug(`Audience joker updated: ${totalVotes} total votes`, { percentages, counts });
        setAudienceResults({
          percentages,
          counts,
          totalVotes
        });
      });

      // Store unsubscribe for cleanup
      audienceUnsubscribeRef.current = unsubscribe;

      await useJoker(JOKER_TYPES.AUDIENCE);
      
      return true;
    } catch (error) {
      log.error('Error using audience joker:', error);
      setJokerLoading(prev => ({ ...prev, [JOKER_TYPES.AUDIENCE]: false }));
      return false;
    }
  }, [instancePath, participantId, currentQuestion, currentQuestionIndex, availableJokers, useJoker]);

  /**
   * Best Player Joker - Show what the top-scoring player answered
   * Sets up a listener that waits for the best player to answer
   */
  const useBestPlayerJoker = useCallback(async () => {
    if (!currentQuestion || !availableJokers[JOKER_TYPES.BEST_PLAYER]) return false;
    if (!instancePath) return false;

    setJokerLoading(prev => ({ ...prev, [JOKER_TYPES.BEST_PLAYER]: true }));

    try {
      // Fetch participants fresh from Firebase using RealtimeDBService
      const participantsData = await RealtimeDBService.get(`${instancePath}/participants`) || {};
      
      // Convert to array format
      const participantsList = Object.entries(participantsData).map(([id, data]) => ({ 
        id, 
        ...data 
      }));
      
      // Sort ALL participants by totalScore (including self)
      const sortedByScore = [...participantsList].sort((a, b) => 
        (b.totalScore || b.score || 0) - (a.totalScore || a.score || 0)
      );
      
      const topPlayer = sortedByScore[0];
      
      // Check if current participant IS the best player
      const isSelfBestPlayer = topPlayer && topPlayer.id === participantId;
      
      const otherParticipants = participantsList.filter(p => p.id !== participantId);
      
      if (otherParticipants.length === 0) {
        setBestPlayerAnswer({ noData: true, message: 'Keine anderen Spieler gefunden' });
        await useJoker(JOKER_TYPES.BEST_PLAYER);
        setJokerLoading(prev => ({ ...prev, [JOKER_TYPES.BEST_PLAYER]: false }));
        return true;
      }

      // Get best player from others (excludes self)
      const bestPlayer = sortedByScore.find(p => p.id !== participantId);
      
      // Helper to parse answer data
      const parseAnswer = (answerData) => {
        if (!answerData) return null;
        return answerData.selectedIndices 
          || (answerData.selectedIndex !== undefined ? [answerData.selectedIndex] : null);
      };
      
      // Set up listener for best player's answer path
      const answerPath = `${instancePath}/answers/${currentQuestionIndex}/${bestPlayer.id}`;
      
      // Show "waiting" state initially
      setBestPlayerAnswer({
        playerId: bestPlayer.id,
        playerName: bestPlayer.name || 'Top Player',
        playerScore: bestPlayer.totalScore || bestPlayer.score || 0,
        waiting: true,
        isSelfBestPlayer: isSelfBestPlayer, // Track if user is best player
        message: 'Warte auf Antwort...'
      });
      
      // Listen for the answer using RealtimeDBService
      const unsubscribe = RealtimeDBService.subscribe(answerPath, (answerData) => {
        const answerIndices = parseAnswer(answerData);
        
        if (answerIndices) {
          // Best player has answered! Show their choice
          setBestPlayerAnswer({
            playerId: bestPlayer.id,
            playerName: bestPlayer.name || 'Top Player',
            playerScore: bestPlayer.totalScore || bestPlayer.score || 0,
            answer: answerIndices,
            isSelfBestPlayer: isSelfBestPlayer // Track if user is best player
          });
          setJokerLoading(prev => ({ ...prev, [JOKER_TYPES.BEST_PLAYER]: false }));
        }
        // If no answer yet, keep showing "waiting" state
      });
      
      // Store unsubscribe for cleanup
      bestPlayerUnsubscribeRef.current = unsubscribe;

      await useJoker(JOKER_TYPES.BEST_PLAYER);
      return true;
    } catch (error) {
      log.error('Error using best player joker:', error);
      setJokerLoading(prev => ({ ...prev, [JOKER_TYPES.BEST_PLAYER]: false }));
      return false;
    }
  }, [
    instancePath, 
    participantId, 
    currentQuestion, 
    currentQuestionIndex,
    availableJokers, 
    useJoker
  ]);

  /**
   * Time Extension Joker - Add bonus time
   */
  const useTimeExtensionJoker = useCallback(async () => {
    if (!availableJokers[JOKER_TYPES.TIME_EXTENSION]) return false;

    setJokerLoading(prev => ({ ...prev, [JOKER_TYPES.TIME_EXTENSION]: true }));

    try {
      const bonusSeconds = settings.timeExtensionBonus || 20;
      
      // Update Firebase with bonus time using RealtimeDBService
      if (instancePath) {
        const bonusTimePath = `${instancePath}/participants/${participantId}/bonusTime`;
        await RealtimeDBService.set(bonusTimePath, bonusSeconds);
      }

      // Call the callback to update timer
      if (onTimeExtension) {
        onTimeExtension(bonusSeconds);
      }

      await useJoker(JOKER_TYPES.TIME_EXTENSION);
      return true;
    } catch (error) {
      log.error('Error using time extension joker:', error);
      return false;
    } finally {
      setJokerLoading(prev => ({ ...prev, [JOKER_TYPES.TIME_EXTENSION]: false }));
    }
  }, [instancePath, participantId, settings, availableJokers, useJoker, onTimeExtension]);

  /**
   * Check if a specific joker is available
   */
  const isJokerAvailable = useCallback((jokerType) => {
    return availableJokers[jokerType] === true;
  }, [availableJokers]);

  /**
   * Check if an option should be hidden (50:50 effect)
   */
  const isOptionHidden = useCallback((optionIndex) => {
    return hiddenOptions.includes(optionIndex);
  }, [hiddenOptions]);

  /**
   * Get audience vote percentage for an option
   */
  const getAudienceVote = useCallback((optionIndex) => {
    if (!audienceResults) return null;
    return audienceResults[optionIndex] || 0;
  }, [audienceResults]);

  /**
   * Check if best player selected an option
   */
  const isBestPlayerChoice = useCallback((optionIndex) => {
    if (!bestPlayerAnswer || bestPlayerAnswer.noData || bestPlayerAnswer.notYetAnswered) {
      return false;
    }
    return bestPlayerAnswer.answer.includes(optionIndex);
  }, [bestPlayerAnswer]);

  return {
    // Joker availability
    availableJokers,
    isJokerAvailable,
    initializeJokers,
    
    // Joker actions
    useFiftyFiftyJoker,
    useAudienceJoker,
    useBestPlayerJoker,
    useTimeExtensionJoker,
    
    // Active effects
    hiddenOptions,
    isOptionHidden,
    audienceResults,
    getAudienceVote,
    bestPlayerAnswer,
    isBestPlayerChoice,
    
    // Loading states
    jokerLoading,
    
    // Reset
    resetQuestionEffects,
    
    // Constants for external use
    JOKER_TYPES,
  };
}

export default useJokers;
